import { AutocompleteRenderInputParams } from '@mui/material'
import { useQueries } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import uniqBy from 'lodash/uniqBy'
import { useState, useMemo } from 'react'
import { useAuthenticatedUser, useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { AssigneeEntity } from '@omnea-app/packages/universal/workflow/model/utils/blocks'
import { SimpleUser } from '@omnea-app/users/usersFrontendClient'
import { groupMembersQueryOptions, useGetGroupMembers } from '../useGetGroupMembers'
import { useGetUsersByIds } from '../useGetUsersByIds'
import { useListGroups } from '../useListGroups'
import { useSearchUsers } from '../useSearchUsers'
import { AssigneeAutocompleteUncontrolled } from './AssigneeAutocompleteUncontrolled'
import { AssigneesError } from './types'

export enum AssigneeSourceType {
  USERS = 'users',
  GROUPS = 'groups',
  USERS_GROUPS = 'users-groups',
}

interface Props {
  values: AssigneeEntity[]
  onChange: (newValue: AssigneeEntity[]) => void
  title?: string
  label?: string | React.ReactNode
  required?: boolean
  disabled?: boolean
  additionalOptions?: AssigneeEntity[]
  isFormTemplatesLoading?: boolean
  type?: AssigneeSourceType
  error?: AssigneesError
  fullWidth?: boolean
  placeholder?: string
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  onBlur?: () => void
  helperText?: string
  excludeUserId?: string
  setAssigneesError?: (error: AssigneesError) => void
  onlyAdditionalOptions?: boolean
  allowedAssignees?: AssigneeEntity[]
  allowSingleAssigneeOnly?: boolean
  hiddenAssigneeIds?: string[]
  hideUuidInLabel?: boolean
  onAddValue?: (value: string) => void
  onAddText?: string
  userServerSideFilter?: boolean
  limitToGroups?: { id: string; name: string }[]
  minSearchLength?: number
  initialOpenState?: boolean
  isAiPrefilled?: boolean
  isOptionLocked?: (option: AssigneeEntity) => boolean
  lockedOptionTooltip?: string
}

type UseAssigneeOptionsProps = {
  currentValueIds: string[]
  type: AssigneeSourceType
  onlyAdditionalOptions?: boolean
  additionalOptions?: AssigneeEntity[]
  excludeUserId?: string
  allowedAssignees?: AssigneeEntity[]
  hiddenAssigneeIds?: string[]
  searchQuery: string
  limitToGroups?: { id: string; name: string }[]
  minSearchLength?: number
}

// Module-level so its reference is stable and useQueries only re-runs it when a result changes
const combineGroupMembers = (results: ReturnType<typeof useGetGroupMembers>[]) => ({
  // One group failing to load must not hide the members of the groups that did
  groupMembers: results.flatMap((result) => result.data?.members ?? []),
  isGroupMembersLoading: results.some((result) => result.isLoading),
})

export const useAssigneeOptions = ({
  type,
  currentValueIds,
  allowedAssignees,
  hiddenAssigneeIds,
  onlyAdditionalOptions,
  additionalOptions,
  excludeUserId,
  searchQuery,
  limitToGroups,
  minSearchLength = 1,
}: UseAssigneeOptionsProps) => {
  const isLimitedToGroups = !isEmpty(limitToGroups)
  const useServerFiltering = isEmpty(allowedAssignees) && !isLimitedToGroups

  const { getToken } = useAuthenticatedUser()
  const isSupplier = useIsSupplierRole()
  // A user in any of the groups is offered, not only those in all of them
  const { groupMembers, isGroupMembersLoading } = useQueries({
    queries: (limitToGroups ?? []).map((group) => groupMembersQueryOptions(getToken, isSupplier, group.id)),
    combine: combineGroupMembers,
  })

  // disable search if we have a set list of allowed assignees or limiting to a group
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isPlaceholderData: isUsersPlaceholderData,
  } = useSearchUsers({
    search: searchQuery,
    limit: 100,
    minSearchLength,
    enabled:
      (type === AssigneeSourceType.USERS_GROUPS || type === AssigneeSourceType.USERS) &&
      isEmpty(allowedAssignees) &&
      !isLimitedToGroups,
  })

  const { data: existingUsersData, isLoading: isCurrentUsersLoading } = useGetUsersByIds({
    userIds: currentValueIds.filter(isUuid),
  })
  const { data: allowedAssigneeUsersData } = useGetUsersByIds({
    userIds: allowedAssignees?.map((u) => u.id).filter(isUuid) ?? [],
  })

  const { data: groupsData, isLoading: isGroupsLoading } = useListGroups()

  const activeUsers = useMemo(() => {
    if (isLimitedToGroups) {
      // When limiting to groups, use only group members
      const groupUsers = groupMembers.map((user) => ({ ...user, isArchived: false }))

      return uniqBy(
        [...(existingUsersData || []), ...groupUsers].filter((user): user is NonNullable<typeof user> => user !== undefined),
        'id'
      )
    }

    // new users via search
    const newUsers = usersData?.filter((user) => !user.isArchived && !currentValueIds.includes(user.id)) || []

    // allowed assignee users (plus validated users) need this to pre fill the drop down if we have a set of
    // assigned users. Any extra get filtered out via the front end logic
    const allowedAssigneesUsers = allowedAssigneeUsersData || []

    const validatedCurrentUsers = existingUsersData || []

    return uniqBy(
      [...validatedCurrentUsers, ...newUsers, ...allowedAssigneesUsers].filter(
        (user): user is NonNullable<typeof user> => user !== undefined
      ),
      'id'
    )
  }, [usersData, currentValueIds, allowedAssigneeUsersData, existingUsersData, isLimitedToGroups, groupMembers])

  const options: AssigneeEntity[] = useMemo(() => {
    let result: AssigneeEntity[] = []
    const allowedIds = allowedAssignees ? allowedAssignees?.map((assignee) => assignee.id) : undefined
    const lowerQuery = searchQuery.toLowerCase()

    const filteredGroups =
      searchQuery && groupsData ? groupsData.filter((group) => group.name.toLowerCase().includes(lowerQuery)) : groupsData

    if ((type === AssigneeSourceType.USERS_GROUPS || type === AssigneeSourceType.USERS) && !onlyAdditionalOptions) {
      result = activeUsers
        .filter((user): user is SimpleUser => {
          if (!user || currentValueIds?.includes(user.id)) return false
          if (user.id === excludeUserId) return false
          if (allowedIds && !allowedIds.includes(user.id)) return false
          if (hiddenAssigneeIds && hiddenAssigneeIds.includes(user.id)) return false
          return true
        })
        .map((user) => {
          return {
            type: AssigneeTypeEnum.USER,
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            value: `${user.firstName} ${user.lastName}`,
            email: user.email,
            isOutOfOffice: user.isOutOfOffice,
            isArchived: user.isArchived,
          }
        })
    }

    if ((type === AssigneeSourceType.USERS_GROUPS || type === AssigneeSourceType.GROUPS) && !onlyAdditionalOptions) {
      filteredGroups
        ?.filter((group) => {
          if (!group || currentValueIds?.includes(group.id)) return false
          if (allowedIds && !allowedIds.includes(group.id)) return false
          if (hiddenAssigneeIds && hiddenAssigneeIds.includes(group.id)) return false
          return true
        })
        .forEach((group) => {
          result.push({
            type: AssigneeTypeEnum.GROUP,
            id: group.id,
            name: group.name,
            value: group.name,
          })
        })
    }

    if (additionalOptions) {
      const filteredAdditionalOptions = searchQuery
        ? additionalOptions.filter(
            (option) =>
              option.value.toLowerCase().includes(lowerQuery) ||
              (option.name != null && option.name.toLowerCase().includes(lowerQuery))
          )
        : additionalOptions

      filteredAdditionalOptions
        .filter((option) => option && !currentValueIds?.includes(option.id))
        .forEach((option) => result.push(option))
    }

    return result.sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }))
  }, [
    type,
    activeUsers,
    groupsData,
    currentValueIds,
    allowedAssignees,
    onlyAdditionalOptions,
    additionalOptions,
    excludeUserId,
    searchQuery,
    hiddenAssigneeIds,
  ])

  return {
    options,
    isUsersLoading: isUsersLoading || isGroupMembersLoading || isCurrentUsersLoading,
    isGroupsLoading,
    // The users are an earlier search's while the current one is in flight, so they may not match it.
    isUsersPlaceholderData,
    users: activeUsers,
    groupsData,
    useServerFiltering,
  }
}

export const AssigneeAutocomplete = ({ onAddValue, onAddText, ...props }: Props) => {
  const [inputValue, setInputValue] = useState('')
  const currentValueIds = props.values.map((item) => item.id)
  const type = props.type || AssigneeSourceType.USERS_GROUPS

  const { isUsersLoading, isGroupsLoading, options, users, groupsData, useServerFiltering } = useAssigneeOptions({
    ...props,
    type,
    currentValueIds,
    searchQuery: inputValue,
  })

  const onAddNew = useMemo(() => (onAddValue ? () => onAddValue(inputValue) : undefined), [onAddValue, inputValue])

  return (
    <AssigneeAutocompleteUncontrolled
      isUsersLoading={isUsersLoading}
      isGroupsLoading={isGroupsLoading}
      isFormTemplatesLoading={props.isFormTemplatesLoading}
      inputValue={inputValue}
      users={users}
      groups={groupsData}
      options={options}
      onInputChange={setInputValue}
      hideGroups={type !== AssigneeSourceType.USERS_GROUPS}
      onAddNew={onAddNew}
      addNewText={onAddText}
      userServerSideFilter={useServerFiltering}
      label={props.label}
      required={props.required}
      {...props}
    />
  )
}
